import { Box, Typography, Button, useTheme } from '@mui/material';

const pillars = [
  {
    icon: '📊',
    title: 'Scaled organic playbook',
    body: 'Hundreds of creator videos per month, briefed against a hook × angle × format matrix tuned to your ICP. Every video has a hypothesis — not "see what sticks."',
  },
  {
    icon: '🔥',
    title: 'Warmed-up creator account network',
    body: "We don't just produce content — we post it. From persona-matched TikTok accounts we've warmed for reach. Most agencies don't have this. Most in-house teams take 6–9 months to build it.",
  },
  {
    icon: '📡',
    title: 'Per-creator MMP attribution',
    body: 'AppsFlyer, Adjust, Branch. Every creator and every account gets their own deep link. You see installs and CPI broken down by creator. We double down on winners, cut underperformers.',
  },
  {
    icon: '📋',
    title: 'Custom performance reporting',
    body: 'Built around the metric that matters to you — installs, CPI, or blended ROAS. Your Growth Operator delivers a weekly variant leaderboard — not a dashboard you have to chase.',
  },
];

const LPSectionInfluencers: React.FC = () => {
  const theme = useTheme();

  return (
    <Box
      id="system"
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        mt: 2,
        pt: 10,
        pb: 10,
        [theme.breakpoints.down('md')]: {
          mt: 0,
          pt: 10
        }
      }}
    >
      {/* Section header */}
      <Box textAlign="center" mb={5} sx={{ width: '100%', maxWidth: 1200 }}>
        <Typography
          fontFamily="Inter"
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            letterSpacing: '2.5px',
            textTransform: 'uppercase',
            color: '#0CAECF',
            mb: 2
          }}
        >
          What you&apos;re actually buying
        </Typography>

        <Typography
          variant="h4"
          fontFamily="Inter"
          sx={{
            fontWeight: 700,
            fontSize: '41.8px',
            lineHeight: 1,
            color: '#FFFFFF',
            mb: 4,
            [theme.breakpoints.down('md')]: {
              fontSize: '29px'
            }
          }}
        >
          The Saagas System
        </Typography>

        <Typography
          fontFamily="Inter"
          sx={{
            width: '100%',
            fontWeight: 600,
            fontSize: '29.03px',
            lineHeight: '120%',
            letterSpacing: '0px',
            textAlign: 'center',
            color: '#FFFF',
            mx: 'auto',
            mb: 1,
            mt: 2,
            padding: '0 20px',
            [theme.breakpoints.down('md')]: {
              fontSize: '16px',
              fontWeight: 300,
              mx: 'none',
              mb: 1,
              mt: 3
            }
          }}
        >
          The full stack — not a creator marketplace, not a video agency.
        </Typography>

        {/* Sub-headline — Growth Operator */}
        <Typography
          fontFamily="Inter"
          sx={{
            width: '100%',
            fontWeight: 400,
            fontSize: '18px',
            lineHeight: '130%',
            textAlign: 'center',
            color: '#0CAECF',
            mx: 'auto',
            mb: 3,
            padding: '0 20px',
            [theme.breakpoints.down('md')]: {
              fontSize: '14px',
              mb: 2
            }
          }}
        >
          Run by a dedicated Growth Operator on every program.
        </Typography>

        <Typography
          fontFamily="Inter"
          sx={{
            width: '100%',
            fontWeight: 300,
            fontSize: '20px',
            lineHeight: '120%',
            letterSpacing: '0px',
            textAlign: 'center',
            color: '#C0C0C0',
            mx: 'auto',
            padding: '0 20px',
            maxWidth: 700,
            [theme.breakpoints.down('md')]: {
              fontSize: '16px',
              fontWeight: 300,
              color: '#FFFFFF'
            }
          }}
        >
          We source creators, warm accounts for reach, post content, attribute every install back to the creator who drove it, and iterate weekly. End-to-end.
        </Typography>
      </Box>

      {/* 4 Pillar cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
          gap: '1.5rem',
          width: '100%',
          maxWidth: 1280,
          px: { xs: 2, md: 4 },
          mb: 7,
          [theme.breakpoints.down('md')]: {
            gap: '1rem',
            mb: 4
          }
        }}
      >
        {pillars.map(({ icon, title, body }) => (
          <Box
            key={title}
            sx={{
              background: 'linear-gradient(139.56deg, rgba(12,174,207,0.08) -29.1%, rgba(88,15,107,0.08) 96.45%)',
              border: '1px solid rgba(12, 174, 207, 0.25)',
              borderRadius: '12px',
              p: 3,
              textAlign: 'left',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5
            }}
          >
            <Typography sx={{ fontSize: '2rem' }}>{icon}</Typography>
            <Typography
              fontFamily="Inter"
              sx={{ fontWeight: 600, fontSize: '16px', color: '#FFFFFF', lineHeight: '130%' }}
            >
              {title}
            </Typography>
            <Typography
              fontFamily="Inter"
              sx={{ fontWeight: 300, fontSize: '14px', color: '#C0C0C0', lineHeight: '150%' }}
            >
              {body}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* CTA */}
      <Button
        component="a"
        href="https://outlook.office.com/bookwithme/user/eb7d87d7154e461ab27627de6f7d2e9e@saagas.ai?anonymous&ismsaljsauthenabled&ep=plink"
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          px: 4,
          py: 1.5,
          borderRadius: '170px',
          boxShadow: '0px -4px 50px 0px rgba(11,87,208,0.68)',
          fontWeight: 700,
          fontSize: '15px',
          background: 'linear-gradient(163deg, rgb(12, 174, 207) -29.1%, rgb(88, 15, 107) 96.45%)',
          color: 'white',
          textTransform: 'none',
          '&:hover': {
            background: 'linear-gradient(163deg, rgb(12, 160, 200) -29.1%, rgb(80, 10, 100) 96.45%)'
          }
        }}
      >
        Book a Discovery Call →
      </Button>
    </Box>
  );
};

export default LPSectionInfluencers;
